import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { AdvanceService } from "../../../shared/services/advance.service";
import { ActivatedRoute, Router } from "@angular/router";
import { CostService } from "src/app/shared/services/cost.service";
import {
  map,
  tap,
  filter,
  switchMap,
  concatMap,
  shareReplay,
  delay,
} from "rxjs/operators";
import { combineLatest, of, Observable, Subscription, forkJoin } from "rxjs";
import { Cost, CostStatuses } from "src/app/shared/models/cost.model";
import { TableDataSource } from "src/app/shared/components/table/table-data-source";
import { UserService } from "src/app/shared/services/user.service";
import { SelectionModel } from "@angular/cdk/collections";
import { MatSort } from "@angular/material/sort";
import { tableSortFn } from "src/app/shared/utils/sort.util";
import { ConfirmService } from "../../../shared/services/confirm.service";
import { ToastrService } from "ngx-toastr";
import { Redirect } from "../../../shared/utils/redirect.util";
import { AdvanceStatus } from "../../../shared/models/advance.model";
import { CostHistory } from "src/app/shared/models/cost-history.model";

interface CostWithHistory extends Cost {
  history: CostHistory[];
}

@Component({
  selector: "app-advance-costs",
  templateUrl: "./advance-costs.component.html",
})
export class AdvanceCostsComponent implements OnInit, OnDestroy {
  currentUserId: number;
  advanceId = 0;
  costStatusTab: string;
  costs$;
  costsByType$;
  advance$: any;
  advances: any;
  hash: string;
  costsCount$;
  title: string;
  costHistoryLoaded: boolean = false;

  routeParamChangeSubscription: Subscription;

  dataSource = new TableDataSource<Cost>();
  selection = new SelectionModel<Cost>(true, []);
  displayedColumns: string[] = [
    "name",
    "status",
    "grossValue",
    "createdAt",
    "updatedAt",
    "action",
  ];

  @ViewChild(MatSort, { static: false }) set sort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  constructor(
    private _advanceService: AdvanceService,
    private _route: ActivatedRoute,
    private _costService: CostService,
    private userService: UserService,
    private confirmService: ConfirmService,
    private toastrService: ToastrService,
    private redirect: Redirect
  ) {}

  ngOnInit() {
    this.currentUserId = this.userService.getCurrentUserId();
    // Detects if route params changed ( advanceId and costStatusTab )
    this._route.paramMap.subscribe((paramAsMap) => {
      // this.currentPage = 0

      // Take params. There has to be local advanceId variable to check if advanceId in URL has changed.
      const advanceId = +paramAsMap.get("advanceId");
      this.costStatusTab = paramAsMap.get("costStatusTab");
      // If advanceId in URL changed, fetch for all costs and advance details
      if (advanceId !== this.advanceId) {
        this.advance$ = this._advanceService.fetchAdvance(advanceId).pipe(
          tap((advance: any) => {
            const person = advance.employee.person;
            this.title = advance.name;
            if (this.currentUserId !== advance.employee.id) {
              this.title =
                person.firstName + " " + person.lastName + ": " + advance.name;
            }
          })
        );
        this.costs$ = this._costService
          .fetchAdvanceCosts(advanceId)
          .pipe(shareReplay());
      }

      // After clicking on tab it filters out costs with right costStatus

      this.fetchData();

      this.advanceId = advanceId;

      this.costsCount$ = this.costs$.pipe(
        map((costs: Cost[]) =>
          costs.reduce(
            (totals, cost) => ({
              ...totals,
              [cost.status.name]: (totals[cost.status.name] || 0) + 1,
            }),
            {}
          )
        ),
        map((tabs) => {
          Object.values(CostStatuses).map((status) => {
            tabs[status] = tabs[status] || 0;
          });
          tabs["progress"] =
            tabs[CostStatuses.New] +
            tabs[CostStatuses.InApproval] +
            tabs[CostStatuses.InMeritoricalApproval] +
            tabs[CostStatuses.InFormalApproval] +
            tabs[CostStatuses.ReadyToReturn] +
            tabs[CostStatuses.Returned] +
            tabs[CostStatuses.Approved] +
            tabs[CostStatuses.Printed] +
            tabs[CostStatuses.Exported];
          return tabs;
        })
      );
    });
  }

  ngOnDestroy() {
    if (this.routeParamChangeSubscription) {
      this.routeParamChangeSubscription.unsubscribe();
    }
  }

  removeCost(cost: Cost) {
    if (cost.canRemove) {
      this.confirmService
        .openConfirmDialog(
          "Czy na pewno chcesz usunąć koszt? Zmiana będzie nieodwracalna."
        )
        .afterClosed()
        .subscribe((confirm) => {
          if (confirm) {
            this._costService.removeCost(cost.id).subscribe(
              (res) => {
                this.toastrService.success("Koszty został usunięty.");
                this.redirect.to();
              },
              (_) => this.toastrService.error("Koszt nie może zostać usunięty.")
            );
          }
        });
    }
  }

  async fetchData() {
    this.costsByType$ = this.costs$.pipe(
      map((costs: Cost[]) =>
        costs.filter((cost) => {
          let hasStatus = false;
          this.costStatusTab === "progress"
            ? (hasStatus =
                cost.status.name !== CostStatuses.Settled &&
                cost.status.name !== CostStatuses.Rejected &&
                cost.status.name !== CostStatuses.Canceled)
            : (hasStatus = cost.status.name === this.costStatusTab);
          return hasStatus;
        })
      ),
      map((costs: Cost[]) => {
        this.dataSource.data = costs;
        this.dataSource.sortingDataAccessor = tableSortFn;
        this.costHistoryLoaded = true; // Set the flag to true
        return costs;
      })
    );
  }


  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach((row) => this.selection.select(row));
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: Cost): string {
    if (!row) {
      return `${this.isAllSelected() ? "select" : "deselect"} all`;
    }
    return `${this.selection.isSelected(row) ? "deselect" : "select"} row ${
      row.id + 1
    }`;
  }
}
